import { PlayerRatingData, getRatingColorClass, getTrendIndicator } from "@/hooks/usePlayerRatings";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

// Average rating icon - small bar chart (matches LineupPitchView)
const AvgIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 12 12" className={cn("w-2.5 h-2.5", className)}>
    <rect x="1" y="6" width="2" height="5" fill="currentColor" opacity="0.6"/>
    <rect x="5" y="3" width="2" height="8" fill="currentColor" opacity="0.8"/>
    <rect x="9" y="5" width="2" height="6" fill="currentColor" opacity="0.7"/>
  </svg>
);

// Golden star icon - separator/identifier for game rating (matches LineupPitchView)
const GameRatingStar = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 12 12" className={cn("w-2.5 h-2.5", className)}>
    <path 
      d="M6 1 L7.2 4.2 L10.5 4.5 L8 6.8 L8.8 10 L6 8.2 L3.2 10 L4 6.8 L1.5 4.5 L4.8 4.2 Z" 
      fill="#FFD700" 
      stroke="#DAA520" 
      strokeWidth="0.3"
    />
  </svg>
);

// Trend arrow UP - performance above expected (green) - THICK SVG
const TrendUpArrow = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 12 12" className={cn("w-2.5 h-2.5", className)}>
    <path d="M6 2 L6 10 M6 2 L3 5 M6 2 L9 5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Trend arrow DOWN - performance below expected (red) - THICK SVG
const TrendDownArrow = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 12 12" className={cn("w-2.5 h-2.5", className)}>
    <path d="M6 10 L6 2 M6 10 L3 7 M6 10 L9 7" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Trend arrow NEUTRAL - expected performance (yellow) - THICK SVG
const TrendNeutralArrow = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 12 12" className={cn("w-2.5 h-2.5", className)}>
    <path d="M2 6 L10 6 M10 6 L7 3 M10 6 L7 9" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

interface PlayerRatingBadgeProps {
  ratingData: PlayerRatingData | undefined;
  isLive: boolean;
  hasFinishedSnapshots?: boolean;
  compact?: boolean;
}

export function PlayerRatingBadge({ 
  ratingData, 
  isLive, 
  hasFinishedSnapshots = false,
  compact = false 
}: PlayerRatingBadgeProps) {
  const { t } = useTranslation('analysis');
  
  if (!ratingData) return null;
  
  const hasLiveRating = ratingData.currentRating !== null;
  const hasHistorical = ratingData.avgRating !== null;
  
  if (!hasLiveRating && !hasHistorical) return null;
  
  const showMatchRating = (isLive || hasFinishedSnapshots) && hasLiveRating;
  const trend = showMatchRating ? getTrendIndicator(ratingData.trend) : null;
  
  const badge = (
    <span className={cn(
      "inline-flex items-center gap-1.5 font-medium tabular-nums",
      compact ? "text-[10px]" : "text-xs"
    )}>
      {hasHistorical && (
        <span className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground opacity-80">
          <AvgIcon className="text-muted-foreground" />
          {ratingData.avgRating!.toFixed(1)}
        </span>
      )}
      
      {hasHistorical && showMatchRating && (
        <GameRatingStar />
      )}
      
      {showMatchRating && (
        <span className={cn(
          "inline-flex items-center gap-0.5 text-sm font-bold",
          getRatingColorClass(ratingData.currentRating!)
        )}>
          {!hasHistorical && <GameRatingStar />}
          {ratingData.currentRating!.toFixed(1)}
          {trend && trend.icon && (
            <span className={cn(trend.colorClass)}>
              {trend.icon === '↑' && <TrendUpArrow />}
              {trend.icon === '↓' && <TrendDownArrow />}
              {trend.icon === '→' && <TrendNeutralArrow />}
            </span>
          )}
        </span>
      )}
    </span>
  );
  
  const tooltipContent = (
    <div className="space-y-1 text-xs">
      {showMatchRating && ratingData.currentRating !== null && (
        <p className="font-semibold">
          {t('playerRating.gameRating', { rating: ratingData.currentRating.toFixed(1) })}
          {ratingData.snapshotMinute && (
            <span className="text-muted-foreground ml-1">
              ({ratingData.snapshotMinute}')
            </span>
          )}
        </p>
      )}
      {hasHistorical && (
        <p className={showMatchRating ? "text-muted-foreground" : "font-semibold"}>
          {t('playerRating.average', { count: ratingData.gamesPlayed, rating: ratingData.avgRating?.toFixed(1) })}
        </p>
      )}
      {trend && trend.label && (
        <p className={trend.colorClass}>{trend.label}</p>
      )}
    </div>
  );
  
  return (
    <TooltipProvider>
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <span className="cursor-help">{badge}</span>
        </TooltipTrigger>
        <TooltipContent side="top">
          {tooltipContent}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
